import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../../../contexts/hooks';
import { NoDataPanel, ModalConfirmation } from '../../..';
import InformedConsentModal from './modals/InformedConsentModal';
import StepForm from './Functions/StepForm';
import { formatMessage } from 'devextreme/localization';
import { useEffect, useMemo, useState } from 'react';
import { useApiInformedConsents } from '../../../../api/api.informedConsents';
import { useScreenSize } from '../../../../utils/media-query';
import { Button, LoadPanel, ScrollView, Toolbar, Tooltip, ContextMenu, Popup } from 'devextreme-react';
import { Item } from 'devextreme-react/toolbar';
import { shallowEqual } from 'react-redux';
import { Pin, UnPin } from '../../../../images/icons';
import { setPanelPined, setPanelVisible } from '../../../../contexts/slices/orders';
import { useActiveTab } from '../../../../hooks/useActiveTab';
import { ModalReportConsentInform } from '../../reporting/ModalReportInformedConsent';
import { TypeConsentStatus } from '../../../../types/types.informedConsents';

const Index = () => {
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const [informedConsents, setInformedConsents] = useState<any[]>([]);
  const [informedConsentsByOrder, setInformedConsentsByOrder] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const [selectedConsent, setSelectedConsent] = useState<any | null>(null);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const { getConsents, getConsentByIdOrder, CreateConsentimientoByOrder } = useApiInformedConsents();
  const [showModal, setShowModal] = useState(false);
  const [showModalReport, setShowModalReport] = useState(false);
  const [modalEstado, setModalEstado] = useState<number>(0);
  // view-only popup state for signed/authorized consents
  const [viewDataModalVisible, setViewDataModalVisible] = useState(false);
  const [viewTemplate, setViewTemplate] = useState<any[] | null>(null);
  const [viewInitialValues, setViewInitialValues] = useState<any | null>(null);
  const currentOrder = useAppSelector((x) => x.globalOrders.CurrentOrder);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const dispatch = useAppDispatch();
  const isActiveTab = useActiveTab(8);
  useEffect(() => {
    if (isActiveTab && idOrd && idOrd !== -1) {
      setIsLoading(true);
      (async () => {
        try {
          const [allConsents, consentsByOrder] = await Promise.all([getConsents(), getConsentByIdOrder(idOrd)]);
          const filteredConsents = (allConsents || []).filter((consent: any) => currentOrder.StudiesData.some((study: any) => study.Es_Id === consent.Cst_Es_Id) || !consent.Cst_Es_Id);
          setInformedConsents(filteredConsents || []);
          setInformedConsentsByOrder(consentsByOrder || []);
        } catch (err) {
          console.error('Error fetching informed consents', err);
          setInformedConsents([]);
          setInformedConsentsByOrder([]);
        } finally {
          setIsLoading(false);
        }
      })();
    } else {
      setInformedConsents([]);
    }
  }, [idOrd, isActiveTab]);

  const fetchInformedConsents = async (idOrder) => {
    const res = await getConsentByIdOrder(idOrder);
    setInformedConsentsByOrder(res || []);
  };

  const openViewDataModal = (report: any, orderConsent: any) => {
    // parse template (prefer saved version from orderConsent when available)
    let tpl: any[] | null = null;
    try {
      if (orderConsent?.Cst_Plantilla) {
        tpl = typeof orderConsent.Cst_Plantilla === 'string' ? JSON.parse(orderConsent.Cst_Plantilla) : orderConsent.Cst_Plantilla;
      } else if (report?.Cst_Plantilla) {
        tpl = typeof report.Cst_Plantilla === 'string' ? JSON.parse(report.Cst_Plantilla) : report.Cst_Plantilla;
      }
    } catch (err) {
      tpl = null;
    }
    let initVals: any = null;
    try {
      initVals = orderConsent?.Roc_Plantilla ? (typeof orderConsent.Roc_Plantilla === 'string' ? JSON.parse(orderConsent.Roc_Plantilla) : orderConsent.Roc_Plantilla) : null;
    } catch (err) {
      initVals = null;
    }
    setViewTemplate(tpl);
    setViewInitialValues(initVals);
    setSelectedConsent(report);
    setModalEstado(orderConsent?.Roc_Estado ?? 0);
    setViewDataModalVisible(true);
  };

  // Determinar columnas según tamaño de pantalla - CORREGIDO
  const gridColumns = useMemo(() => {
    if (isXSmall) return 1;
    if (isSmall) return 1;
    if (isMedium) return 2;
    return 3; // isLarge o más grande = 3 columnas
  }, [isXSmall, isSmall, isMedium]);

  if (!idOrd || idOrd === -1) {
    return <NoDataPanel title={formatMessage('OrdNoDataTitle')} description={formatMessage('OrdNoDataDescription')} />;
  }

  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };

  return (
    <div style={{ marginTop: 5, height: '100%' }}>
      <div className='reports-page' style={{ padding: 15 }}>
        <Toolbar>
          <Item location='before'>
            <div className='grid-header'>Consentimientos</div>
          </Item>

          <Item location='before' locateInMenu='auto'>
            <div className='separator' />
          </Item>
          <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
            <Button id='Showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
            <Tooltip target={'#Showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
            </Tooltip>
          </Item>
        </Toolbar>
        {!isLoading && informedConsents.length > 0 && (
          <div style={{ height: 'calc(100vh - 300px)', padding: '5px', overflow: 'auto', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
            <ScrollView height={'calc(100vh - 360px)'}>
              <div className='reports-section'>
                <div className='reports-grid' style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)`, marginTop: 5 }}>
                  {informedConsents.map((report, index) => {
                    // find corresponding created consent for this order (if any)
                    const orderConsent = informedConsentsByOrder?.find((x: any) => x.Roc_Cst_Id === report.Cst_Id);
                    //setSelectedRpm(orderConsent)

                    const created = !!orderConsent;

                    // determine status and colors based on Roc_Estado when created
                    const estado: TypeConsentStatus = orderConsent?.Roc_Estado ?? TypeConsentStatus.Pending;
                    // Colors mapping per your spec
                    const COLOR_AZUL = '#65ABF2';
                    const COLOR_AMARILLO = '#F6C024';
                    const COLOR_VERDE = '#49A54A';

                    let statusColor = '#9e9e9e';
                    let badgeText = report.Cst_Descripcion;
                    if (!created) {
                      statusColor = '#9e9e9e'; // not created -> grey
                    } else {
                      switch (estado) {
                        case TypeConsentStatus.Pending:
                          statusColor = COLOR_AZUL;
                          badgeText = 'Creado';
                          break;
                        case TypeConsentStatus.Filled:
                          statusColor = COLOR_AMARILLO;
                          badgeText = 'Diligenciado';
                          break;
                        case TypeConsentStatus.SignedByPatient:
                          statusColor = COLOR_AMARILLO;
                          badgeText = 'Firmado';
                          break;
                        case TypeConsentStatus.SignedByAuthorized:
                          statusColor = COLOR_VERDE;
                          badgeText = 'Autorizado';
                          break;
                        case TypeConsentStatus.Completed:
                          statusColor = COLOR_VERDE;
                          badgeText = 'Completado';
                          break;
                      }
                    }

                    // pick icon class from consent config if present
                    const iconClass = report?.Cst_Icon ? `dx-icon dx-icon-${report.Cst_Icon}` : 'dx-icon dx-icon-doc';
                    const iconBg = report?.Cst_Color ? report.Cst_Color : statusColor;
                    const cardBg = created ? undefined : '#e0e0e0';

                    return (
                      <div
                        key={index}
                        id={`consent-card-${report.Cst_Id}-${index}`}
                        className='report-card'
                        onClick={() => {
                          if (!created) {
                            setSelectedConsent(report);
                            setConfirmVisible(true);
                          } else {
                            setSelectedReport(orderConsent.Consentimiento.Reporte.Rp_Nombre);
                            setModalEstado(orderConsent?.Roc_Estado ?? 0);
                            setSelectedConsent(report);
                            if (orderConsent?.Roc_Estado >= 3) setShowModalReport(true);
                            else setShowModal(true);
                          }
                        }}
                        style={{ backgroundColor: cardBg, cursor: created ? 'default' : 'pointer' }}>
                        <div className='card-icon' style={{ backgroundColor: iconBg }}>
                          <i className={iconClass} />
                        </div>
                        <div className='card-content'>
                          <h4 className='card-title'>{report.Cst_Nombre}</h4>
                          {/* description under the name */}
                          <div className='card-description' style={{ marginTop: 6, color: '#666', fontSize: 13 }}>
                            {report.Cst_Descripcion}
                          </div>
                          <span className={`card-badge ${created ? 'badge-report' : 'badge-disabled'}`} style={created ? { backgroundColor: statusColor, color: '#fff' } : undefined}>
                            {created ? badgeText : 'No creado'}
                          </span>
                        </div>
                        <div className='card-action'>
                          <i className='dx-icon dx-icon-chevronright'></i>
                        </div>
                        </div>
                    );
                  })}
                </div>
              </div>
            </ScrollView>
          </div>
        )}

        {/* ContextMenu components rendered outside the grid so they don't occupy grid cells */}
        {informedConsents.map((report, index) => {
          const orderConsent = informedConsentsByOrder?.find((x: any) => x.Roc_Cst_Id === report.Cst_Id);
          const created = !!orderConsent;
          if (!created || (orderConsent?.Roc_Estado ?? 0) < 3) return null;
          return (
            <ContextMenu
              key={`menu-${report.Cst_Id}-${index}`}
              width={'auto'}
              target={`#consent-card-${report.Cst_Id}-${index}`}
              dataSource={[{ text: 'Ver datos solicitados' }]}
              showEvent='dxcontextmenu'
              onItemClick={() => openViewDataModal(report, orderConsent)}
            />
          );
        })}

        {/* Popup for view-only display of requested data (uses StepForm in readOnly mode) */}
        <Popup
          visible={viewDataModalVisible}
          onHiding={() => {
            setViewDataModalVisible(false);
            setViewTemplate(null);
            setViewInitialValues(null);
            setSelectedConsent(null);
            setModalEstado(0);
          }}
          showTitle={true}
          title={`Datos solicitados - ${selectedConsent?.Cst_Nombre || ''}`}
          width={720}
          height={'auto'}
          showCloseButton={true}
          fullScreen={isSmall || isXSmall}
          shading={true}>
          {viewTemplate && viewTemplate.length > 0 ? (
            <div style={{ padding: 8 }}>
              <StepForm template={viewTemplate} initialValues={viewInitialValues} readOnly={true} onCancel={() => {}} onSubmit={async () => {}} />
            </div>
          ) : (
            <div style={{ padding: 16 }}>No hay datos para mostrar.</div>
          )}
        </Popup>

        {/* Loading overlay while fetching consents */}
        <LoadPanel visible={isLoading} showPane={false} position={{ of: '.reports-page' }} />
        <ModalConfirmation
          message={'Desea crear este consentimiento'}
          visible={confirmVisible}
          onConfirm={async () => {
            if (!selectedConsent || !idOrd) {
              setConfirmVisible(false);
              setSelectedConsent(null);
              return;
            }
            try {
              // payload: include IdOrder and IdCst (backend may expect different names; include both Cst_Id to be safe)
              const payload = { IdOrd: idOrd, IdCst: selectedConsent.Cst_Id };
              await CreateConsentimientoByOrder(payload);
              // refresh list of consents for this order
              await fetchInformedConsents(idOrd);
            } catch (err) {
              console.error('Error creating consent for order', err);
            } finally {
              setConfirmVisible(false);
              setSelectedConsent(null);
            }
          }}
          onCancel={() => {
            setConfirmVisible(false);
            setSelectedConsent(null);
          }}
        />
        <InformedConsentModal
          visible={showModal && selectedConsent !== null && modalEstado < 3}
          onClose={() => {
            setShowModal(false);
            setSelectedConsent(null);
            setModalEstado(0);
          }}
          template={selectedConsent}
          estado={modalEstado}
          currentData={informedConsentsByOrder.find((x: any) => x.Roc_Cst_Id === (selectedConsent ? selectedConsent.Cst_Id : -1))}
          onUpdateDB={async (result: boolean) => {
            if (idOrd && result) await fetchInformedConsents(idOrd);
          }}
        />
        <ModalReportConsentInform
          idOrden={idOrd}
          IdCst={selectedConsent?.Cst_Id ?? -1}
          visible={showModalReport && modalEstado >= 3}
          setVisible={() => {
            setShowModalReport(false);
          }}
          reportName={selectedReport ?? ''}
        />
      </div>
    </div>
  );
};

export default Index;
